import * as React from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import './App.css'
import { GetCookieFunction } from './utils/util'
import {
    Login,
    Logout,
    PageNotFound,
    Navigation,
    PageUnauthorized,
    ReviewSystems,
    PastMedicalHistory,
    UserHome,
    PatientProfile,
    TreatmentNotes,
    DialysisOrder,
    Medication,
    Allergies,
    CheckIn,
    CheckOut,
    DoctorComments,
    ChangePassword,
    Vitals,
    PatientLookup,
    PatientInfo,
    EditPersonalProfile,
    RegistrationInfo,
    StartTreatment,
    RecordData,
    SurgicalHistory,
    FamilySocialHistory,
    AssessmentPlan,
    CreateUser,
    StaffLookup,
    EditUser,
    PatientVisitHistory,
    Reports,
    CenterSettings,
    Scheduling,
    WeeklySchedule,
    DailySchedule,
    ScheduleConflicts,
    PatientSchedule,
    Help
} from './hub'

const App = () => {

    return (
            <div>
            <Navigation />
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/forbidden" element={<PageUnauthorized />} />
                
                <Route element={<RequireLoginAuth />}>
                    <Route path="/" element={<UserHome />} />
                    <Route path="/admin/users/changepass" element={<ChangePassword />} />
                    <Route path="/patients/lookup" element={<PatientLookup />} />
                    <Route path="/patients/:patientId/profile" element={<PatientProfile />} />
                    <Route path="/patients/create/personal" element={<RegistrationInfo />} />
                    <Route path="/patients/:patientId/visit-history" element={<PatientVisitHistory />} />
                    <Route path="/patients/:patientId/schedule" element={<PatientSchedule />} />
                    <Route path="/schedule" element={<WeeklySchedule />} />
                    <Route path="/schedule/daily" element={<DailySchedule />} />
                    <Route path="/schedule/conflicts" element={<ScheduleConflicts />} />
                    <Route path="/help" element={<Help />} />
                </Route>

                <Route element={<RequireDoctorAuth />}>
                    <Route path="/doctor" element={<UserHome />} />
                    <Route path="/patients/:patientId/dialysisorder" element={<DialysisOrder />} />
                    <Route path="/patients/:patientId/notes/:step" element={<TreatmentNotes />} />
                </Route>

                <Route element={<RequireNurseAuth />}>
                    <Route path="/nurse" element={<UserHome />} />
                </Route>

                <Route element={<RequireAdminAuth />}>
                    <Route path="/admin/users/create" element={<CreateUser />} />
                    <Route path="/admin/users/lookup" element={<StaffLookup />} />
                    <Route path="/admin/users/update" element={<EditUser />} />
                    <Route path="/admin/reports" element={<Reports />} />
                    <Route path="/admin/center" element={<CenterSettings />} />
                    <Route path="/admin/scheduling" element={<Scheduling />} />
                </Route>

                <Route element={<RequireClinicalOrAdminAuth />}>
                    <Route path="/patients/:patientId/editProfile" element={<EditPersonalProfile />} />
                    <Route path="/patients/:patientId/patientInfo" element={<PatientInfo />} />
                </Route>

                <Route element={<RequireDoctorOrNurseAuth />}>
                    <Route path="/patients/:patientId/allergies" element={<Allergies />} />
                    <Route path="/patients/:patientId/medications" element={<Medication />} />
                    <Route path="/patients/:patientId/surgeries" element={<SurgicalHistory/>} />
                    <Route path="/patients/:patientId/familySocialHistory" element={<FamilySocialHistory />} />
                    <Route path="/patients/:patientId/assessmentPlan" element={<AssessmentPlan />} />
                    <Route path="/patients/:patientId/reviewSystems" element={<ReviewSystems />} />
                    <Route path="/patients/:patientId/pastMedicalHistory" element={<PastMedicalHistory />} />
                    <Route path="/patients/:patientId/checkin" element={<CheckIn />} />
                    <Route path="/patients/:patientId/checkout" element={<CheckOut />} />
                    <Route path="/patients/:patientId/vitals" element={<Vitals />} />
                    <Route path="/patients/:patientId/recordLabReports/:question_id" element={<RecordData />} />
                    <Route path="/patients/:patientId/doctorsComments" element={<DoctorComments />} />
                    <Route path="/patients/:patientId/starttreatment" element={<StartTreatment />} />
                </Route>
                
                <Route path="/" element={<Login />} />
                <Route path="*" element={<PageNotFound />} />
            </Routes>
            </div>
    );
}

function RequireLoginAuth() {
    // TODO: Verify user is a Doctor
    let user = GetCookieFunction("User").id;
    let token = GetCookieFunction("User").token;
    let location = useLocation();
    //console.log(`Inside RequireLoginAuth. User: ${user}, Token: ${token}`)

    if (!user || !token) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to="/login" state={{ from: location }}  />;
    }

    return <Outlet />;
}

function RequireDoctorAuth() {
    // TODO: Verify user is a Doctor
    let user = GetCookieFunction("User").id;
    let user_type = GetCookieFunction("User").type;
    let token = GetCookieFunction("User").token;
    let location = useLocation();
    //console.log(`Inside RequireDoctorAuth. User: ${user}, Token: ${token}, Type: ${user_type}`)

    if (!user || !token) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to="/login" state={{ from: location }}  />;
    } else if (user_type !== "doctor") {
        // A user who is not a doctor is trying to access a doctor resource.
        // Send them to a bad auth page
        return <Navigate to="/forbidden" state={{ from: location }}  />;
    }

    return <Outlet />;
}

function RequireNurseAuth() {
    // TODO: Verify user is a Doctor
    let user = GetCookieFunction("User").id;
    let user_type = GetCookieFunction("User").type;
    let token = GetCookieFunction("User").token;
    let location = useLocation();
    //console.log(`Inside RequireNurseAuth. User: ${user}, Token: ${token}, Type: ${user_type}`)

    if (!user || !token || user_type !== "nurse") {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to="/login" state={{ from: location }}  />;
    }

    return <Outlet />;
}

// Allows only doctors and nurses
function RequireDoctorOrNurseAuth() {
    let user = GetCookieFunction("User").id;
    let user_type = GetCookieFunction("User").type;
    let token = GetCookieFunction("User").token;
    let location = useLocation();

    if (!user || !token) {
        return <Navigate to="/login" state={{ from: location }}  />;
    } else if (user_type !== "nurse" && user_type !== "doctor") {
        return <Navigate to="/forbidden" state={{ from: location }}  />;
    }

    return <Outlet />;
}

// Allows doctors, nurses, center admins, and executives
function RequireClinicalOrAdminAuth() {
    let user = GetCookieFunction("User").id;
    let user_type = GetCookieFunction("User").type;
    let token = GetCookieFunction("User").token;
    let location = useLocation();

    if (!user || !token) {
        return <Navigate to="/login" state={{ from: location }}  />;
    } else if (user_type !== "nurse" && user_type !== "doctor" && user_type !== "centeradmin" && user_type !== "executive") {
        return <Navigate to="/forbidden" state={{ from: location }}  />;
    }

    return <Outlet />;
}

function RequireAdminAuth() {
    let user = GetCookieFunction("User").id;
    let token = GetCookieFunction("User").token;
    let user_admin = GetCookieFunction("User").admin;
    let user_type = GetCookieFunction("User").type;
    let location = useLocation();

    if (!user || !token) {
        return <Navigate to="/login" state={{ from: location }}  />;
    }

    // Allow access if user has admin flag OR is a centeradmin/executive
    if (user_admin !== 1 && user_type !== "centeradmin" && user_type !== "executive") {
        return <Navigate to="/forbidden" state={{ from: location }}  />;
    }

    return <Outlet />;
}

export default App;
